/**
 * LegalSignPage - Frontend App
 * Il Diario di Bordo - SaaS
 * 
 * Pagina per firmare/accettare documenti legali:
 * - Visualizza contenuto documento
 * - Richiede scroll completo (se configurato)
 * - Firma con checkbox o firma digitale
 * 
 * @version 1.0.0
 * @created Gennaio 2025
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { legalApi } from '../services/legalApi'
import ReactMarkdown from 'react-markdown'
import {
  FileText,
  CheckCircle,
  AlertCircle,
  Loader2,
  ChevronLeft,
  ExternalLink,
  Download,
  Clock,
  AlertTriangle,
  Check,
  ScrollText
} from 'lucide-react'

export default function LegalSignPage() {
  const navigate = useNavigate()
  const { id } = useParams()
  const [searchParams] = useSearchParams()
  const returnUrl = searchParams.get('return') || '/dashboard'
  
  const contentRef = useRef(null)
  
  const [loading, setLoading] = useState(true)
  const [signing, setSigning] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(false)
  
  const [document, setDocument] = useState(null)
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false)
  const [accepted, setAccepted] = useState(false)
  const [timeSpent, setTimeSpent] = useState(0)

  useEffect(() => {
    loadDocument()
  }, [id])

  // Timer per tracciare tempo speso
  useEffect(() => {
    const interval = setInterval(() => {
      setTimeSpent(t => t + 1)
    }, 1000)
    return () => clearInterval(interval)
  }, [])

  // Scroll detection
  const handleScroll = useCallback(() => {
    if (!contentRef.current || hasScrolledToBottom) return
    
    const { scrollTop, scrollHeight, clientHeight } = contentRef.current
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - 50
    
    if (isAtBottom) {
      setHasScrolledToBottom(true)
    }
  }, [hasScrolledToBottom])

  const loadDocument = async () => {
    try {
      setLoading(true)
      const response = await legalApi.getDocument(id)
      setDocument(response.data.data.document)
      
      // Se non richiede scroll completo, considera già scrollato
      if (!response.data.data.document.requiresFullRead) {
        setHasScrolledToBottom(true)
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Errore nel caricamento del documento')
    } finally {
      setLoading(false)
    }
  }

  const handleSign = async () => {
    if (!accepted) {
      setError('Devi accettare il documento per continuare')
      return
    }
    
    if (document?.requiresFullRead && !hasScrolledToBottom) {
      setError('Devi leggere tutto il documento prima di accettare')
      return
    }
    
    try {
      setSigning(true)
      setError(null)
      
      await legalApi.acknowledgeDocument(id, {
        accepted: true,
        fullReadCompleted: hasScrolledToBottom,
        timeSpentSeconds: timeSpent
      })
      
      setSuccess(true)
      
      // Redirect dopo 2 secondi
      setTimeout(() => {
        navigate(returnUrl)
      }, 2000)
      
    } catch (err) {
      setError(err.response?.data?.message || 'Errore nella firma del documento')
    } finally {
      setSigning(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="h-10 w-10 animate-spin text-primary-600 mx-auto mb-4" />
          <p className="text-gray-600">Caricamento documento...</p>
        </div>
      </div>
    )
  }

  if (!document) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md mx-auto text-center p-6">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Documento non trovato
          </h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => navigate(returnUrl)}
            className="text-primary-600 hover:underline"
          >
            Torna indietro
          </button>
        </div>
      </div>
    )
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md mx-auto text-center p-6">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="h-10 w-10 text-green-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Documento firmato!
          </h2>
          <p className="text-gray-600 mb-4">
            Hai accettato "{document.title}" v{document.version}
          </p>
          <p className="text-sm text-gray-500">
            Reindirizzamento in corso...
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(returnUrl)}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <div>
              <h1 className="font-semibold text-gray-900">{document.title}</h1>
              <p className="text-sm text-gray-500">
                Versione {document.version} • {document.typeDisplayName}
              </p>
            </div>
          </div>
          
          {document.signatureDeadline && (
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm ${
              document.isDeadlineExpired 
                ? 'bg-red-100 text-red-700' 
                : document.daysToDeadline <= 3
                  ? 'bg-yellow-100 text-yellow-700'
                  : 'bg-gray-100 text-gray-700'
            }`}>
              <Clock className="h-4 w-4" />
              {document.isDeadlineExpired 
                ? 'Deadline scaduta' 
                : `${document.daysToDeadline} giorni rimasti`
              }
            </div>
          )}
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Error */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {/* Scroll Warning */}
        {document.requiresFullRead && !hasScrolledToBottom && (
          <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center gap-3">
            <ScrollText className="h-5 w-5 text-blue-500 flex-shrink-0" />
            <p className="text-blue-700">
              Scorri fino in fondo al documento per poter accettare
            </p>
          </div>
        )}

        {/* Document Card */}
        <div className="bg-white border rounded-xl overflow-hidden">
          {/* Document Header */}
          <div className="p-6 border-b bg-gray-50">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-primary-100 rounded-lg">
                <FileText className="h-6 w-6 text-primary-600" />
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-semibold text-gray-900">
                  {document.title}
                </h2>
                {document.description && (
                  <p className="text-gray-600 mt-1">{document.description}</p>
                )}
                <p className="text-sm text-gray-500 mt-2">
                  Ultimo aggiornamento: {new Date(document.effectiveDate).toLocaleDateString('it-IT', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                  })}
                </p>
              </div>
            </div>
          </div>

          {/* Document Content */}
          <div 
            ref={contentRef}
            onScroll={handleScroll}
            className="p-6 max-h-[60vh] overflow-y-auto"
          >
            {document.contentSource === 'editor' && document.content?.body ? (
              <div className="prose prose-sm max-w-none">
                <ReactMarkdown>{document.content.body}</ReactMarkdown>
              </div>
            ) : document.contentSource === 'external_url' && document.content?.externalUrl ? (
              <div className="text-center py-8">
                <ExternalLink className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 mb-4">
                  Il documento è disponibile al seguente link:
                </p>
                <a
                  href={document.content.externalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                >
                  <ExternalLink className="h-4 w-4" />
                  Apri Documento
                </a>
              </div>
            ) : document.contentSource === 'pdf' && document.pdf?.url ? (
              <div className="text-center py-8">
                <FileText className="h-12 w-12 text-red-500 mx-auto mb-4" />
                <p className="text-gray-600 mb-4">
                  Il documento è disponibile in formato PDF:
                </p>
                <a
                  href={document.pdf.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  <Download className="h-4 w-4" />
                  Scarica PDF
                </a>
                <p className="text-sm text-gray-500 mt-3">
                  {document.pdf.filename} ({(document.pdf.size / 1024).toFixed(1)} KB)
                </p>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                Contenuto non disponibile
              </div>
            )}
            
            {/* Scroll indicator */}
            {document.requiresFullRead && !hasScrolledToBottom && (
              <div className="text-center py-4 text-gray-400 animate-bounce">
                ↓ Continua a scorrere ↓
              </div>
            )}
          </div>

          {/* Acceptance Section */}
          <div className="p-6 border-t bg-gray-50">
            {document.acceptanceType !== 'view_only' ? (
              <>
                <label className={`flex items-start gap-3 p-4 rounded-lg cursor-pointer transition-colors ${
                  accepted 
                    ? 'bg-green-50 border-2 border-green-500' 
                    : 'bg-white border-2 border-gray-200 hover:border-primary-300'
                } ${(!hasScrolledToBottom && document.requiresFullRead) ? 'opacity-50 cursor-not-allowed' : ''}`}>
                  <input
                    type="checkbox"
                    checked={accepted}
                    onChange={(e) => setAccepted(e.target.checked)}
                    disabled={!hasScrolledToBottom && document.requiresFullRead}
                    className="w-5 h-5 mt-0.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <div>
                    <p className="font-medium text-gray-900">
                      {document.acceptanceType === 'signature' 
                        ? 'Confermo di aver letto e accetto il documento con valore di firma' 
                        : 'Dichiaro di aver letto e accetto i termini del documento'
                      }
                    </p>
                    <p className="text-sm text-gray-500 mt-1">
                      {document.title} - Versione {document.version}
                    </p>
                  </div>
                </label>

                <button
                  onClick={handleSign}
                  disabled={!accepted || signing || (!hasScrolledToBottom && document.requiresFullRead)}
                  className="w-full mt-4 py-3 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {signing ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <Check className="h-5 w-5" />
                  )}
                  {signing ? 'Firma in corso...' : 'Firma e Accetta'}
                </button>
              </>
            ) : (
              <div className="text-center">
                <p className="text-gray-600 mb-4">
                  Questo documento è solo informativo e non richiede firma.
                </p>
                <button
                  onClick={() => navigate(returnUrl)}
                  className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                >
                  Ho capito, continua
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Footer Info */}
        <div className="mt-6 text-center text-sm text-gray-500">
          <p>
            Firmando questo documento accetti le condizioni indicate.
            Per qualsiasi domanda contatta{' '}
            <a href="mailto:supporto@ildiariodibordo.it" className="text-primary-600 hover:underline">
              supporto@ildiariodibordo.it
            </a>
          </p>
        </div>
      </main>
    </div>
  )
}
